package org.zq.astrock_ai.ui.research.gushi.components

import org.zq.astrock_ai.util.formatDecimal

import androidx.compose.foundation.Canvas
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.gestures.detectTapGestures
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableIntStateOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Path
import androidx.compose.ui.graphics.PathEffect
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.graphics.drawscope.DrawScope
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import org.zq.astrock_ai.api.ChartPeriod
import org.zq.astrock_ai.ui.research.gushi.StockColors
import org.zq.astrock_ai.ui.research.gushi.data.matchRecommendCandleIndex
import org.zq.astrock_ai.ui.research.gushi.model.Candle
import org.zq.astrock_ai.ui.research.gushi.model.ChartRecommendMarker
import org.zq.astrock_ai.ui.research.gushi.model.ChartTradeMarker
import kotlin.math.abs
import kotlin.math.floor
import kotlin.math.max
import kotlin.math.min

@Composable
fun StockChart(
    candles: List<Candle>,
    modifier: Modifier = Modifier,
    support: Double? = null,
    resistance: Double? = null,
    showDecorations: Boolean = false,
    lineMode: Boolean = false,
    fiveDayMode: Boolean = false,
    referenceClose: Double? = null,
    tradeMarkers: List<ChartTradeMarker> = emptyList(),
    recommendMarkers: List<ChartRecommendMarker> = emptyList(),
    chartPeriod: ChartPeriod = ChartPeriod.DAY,
    /** 外部控制可见 K 线数量（弹框放大时用）；null 则内部管理 */
    visibleBars: Int? = null,
    onVisibleBarsChange: ((Int) -> Unit)? = null,
    showZoomControls: Boolean = true,
) {
    val ma5 = remember(candles) { movingAverage(candles, 5) }
    val ma10 = remember(candles) { movingAverage(candles, 10) }
    val ma20 = remember(candles) { movingAverage(candles, 20) }
    val ma60 = remember(candles) { movingAverage(candles, 60) }
    var internalVisibleCount by remember(candles.size) { mutableIntStateOf(candles.size) }
    var selectedIndex by remember(candles.size) { mutableStateOf<Int?>(null) }
    var selectedRecommend by remember(candles.size, recommendMarkers) {
        mutableStateOf<ChartRecommendMarker?>(null)
    }

    val markerIndexByAtMs = remember(candles, recommendMarkers, chartPeriod) {
        recommendMarkers.mapNotNull { marker ->
            val idx = matchRecommendCandleIndex(candles, marker.atMs, chartPeriod) ?: return@mapNotNull null
            marker to idx
        }
    }

    val minimumVisible = min(12, candles.size)
    fun setVisibleCount(next: Int) {
        val clamped = next.coerceIn(minimumVisible.coerceAtLeast(1), candles.size.coerceAtLeast(1))
        if (onVisibleBarsChange != null) {
            onVisibleBarsChange(clamped)
        } else {
            internalVisibleCount = clamped
        }
    }
    val visibleCount = visibleBars ?: internalVisibleCount
    val actualVisibleCount = visibleCount.coerceIn(minimumVisible.coerceAtLeast(1), candles.size.coerceAtLeast(1))
    val visibleStart = (candles.size - actualVisibleCount).coerceAtLeast(0)
    val visibleCandles = candles.drop(visibleStart)
    val visibleMa5 = ma5.drop(visibleStart)
    val visibleMa10 = ma10.drop(visibleStart)
    val visibleMa20 = ma20.drop(visibleStart)
    val visibleMa60 = ma60.drop(visibleStart)
    val selected = selectedIndex?.let(candles::getOrNull)
    val selectedPreviousClose = if (lineMode && !fiveDayMode) {
        referenceClose
    } else {
        selectedIndex
            ?.takeIf { it > 0 }
            ?.let { candles[it - 1].close }
    }
    val intradayHigh = remember(candles, lineMode, fiveDayMode) {
        candles.takeIf { lineMode && !fiveDayMode && it.isNotEmpty() }?.maxByOrNull { it.high }
    }
    val intradayLow = remember(candles, lineMode, fiveDayMode) {
        candles.takeIf { lineMode && !fiveDayMode && it.isNotEmpty() }?.minByOrNull { it.low }
    }

    fun lastMa(series: List<Double?>): String =
        series.lastOrNull { it != null }?.let { formatDecimal(it) } ?: "--"

    Column(modifier.fillMaxSize()) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 14.dp, vertical = 7.dp),
            horizontalArrangement = Arrangement.spacedBy(12.dp),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            if (lineMode) {
                val last = candles.lastOrNull()?.close
                val lastAvg = candles.lastOrNull()?.avg
                Legend("现价: ${last?.let { formatDecimal(it) } ?: "--"}", StockColors.PrimaryBlue)
                Legend("均价: ${lastAvg?.let { formatDecimal(it) } ?: "--"}", StockColors.Orange)
                if (recommendMarkers.isNotEmpty()) {
                    Legend("荐", Color(0xFFFF8C00))
                }
            } else {
                Legend("MA5: ${lastMa(ma5)}", Color(0xFFF97316))
                Legend("MA10: ${lastMa(ma10)}", Color(0xFF3B82F6))
                Legend("MA20: ${lastMa(ma20)}", Color(0xFFA855F7))
                Legend("MA60: ${lastMa(ma60)}", Color(0xFF22C55E))
                if (recommendMarkers.isNotEmpty()) {
                    Legend("荐", Color(0xFFFF8C00))
                }
            }
            Spacer(Modifier.weight(1f))
            if (showZoomControls) {
                ZoomControl(
                    text = "−",
                    enabled = actualVisibleCount < candles.size,
                    onClick = {
                        setVisibleCount(min(candles.size, max(actualVisibleCount + 1, actualVisibleCount * 4 / 3)))
                        selectedIndex = null
                    },
                )
                Text(
                    "${actualVisibleCount}/${candles.size}",
                    color = StockColors.TextSecondary,
                    fontSize = 10.sp,
                    maxLines = 1,
                )
                ZoomControl(
                    text = "+",
                    enabled = actualVisibleCount > minimumVisible,
                    onClick = {
                        setVisibleCount(max(minimumVisible, min(actualVisibleCount - 1, actualVisibleCount * 3 / 4)))
                        selectedIndex = null
                    },
                )
                if (actualVisibleCount < candles.size) {
                    Text(
                        "全览",
                        color = StockColors.PrimaryBlue,
                        fontSize = 10.sp,
                        modifier = Modifier.clickable {
                            setVisibleCount(candles.size)
                            selectedIndex = null
                        },
                    )
                }
            }
            if (!lineMode) {
                Text(
                    "压力 ${resistance?.let { formatDecimal(it) } ?: "--"}",
                    color = StockColors.UpRed,
                    fontSize = 11.sp,
                    maxLines = 1,
                )
                Text(
                    "支撑 ${support?.let { formatDecimal(it) } ?: "--"}",
                    color = StockColors.DownGreen,
                    fontSize = 11.sp,
                    maxLines = 1,
                )
            }
        }

        if (intradayHigh != null && intradayLow != null) {
            IntradayRangeInfo(
                high = intradayHigh.high,
                low = intradayLow.low,
                referenceClose = referenceClose,
            )
        }

        selectedRecommend?.let { marker ->
            RecommendPickInfo(
                marker = marker,
                currentPrice = candles.lastOrNull()?.close,
            )
        }

        if (selected != null) {
            SelectedCandleInfo(
                candle = selected,
                previousClose = selectedPreviousClose,
            )
        }

        BoxWithConstraints(
            modifier = Modifier
                .weight(1f)
                .fillMaxWidth()
                .padding(horizontal = 12.dp, vertical = 4.dp)
        ) {
            Canvas(
                Modifier
                    .fillMaxSize()
                    .pointerInput(visibleCandles, visibleStart, markerIndexByAtMs) {
                        detectTapGestures { position ->
                            if (visibleCandles.isEmpty() || size.width <= 0) return@detectTapGestures
                            val step = size.width.toFloat() / visibleCandles.size
                            val localIndex = floor(position.x / step)
                                .toInt()
                                .coerceIn(0, visibleCandles.lastIndex)
                            val absIndex = visibleStart + localIndex
                            selectedIndex = absIndex
                            selectedRecommend = markerIndexByAtMs
                                .filter { it.second == absIndex }
                                .maxByOrNull { it.first.atMs }
                                ?.first
                        }
                    },
            ) {
                if (visibleCandles.isEmpty()) return@Canvas

                val priceHeight = size.height * 0.74f
                val volumeTop = priceHeight + 16f
                val volumeHeight = size.height - volumeTop

                val visibleRecs = markerIndexByAtMs.filter { (_, idx) ->
                    idx in visibleStart until visibleStart + visibleCandles.size
                }
                var maxPrice = visibleCandles.maxOf { maxOf(it.high, it.avg ?: it.high) }
                var minPrice = visibleCandles.minOf { minOf(it.low, it.avg ?: it.low) }
                visibleRecs.forEach { (marker, _) ->
                    if (marker.price > 0) {
                        maxPrice = max(maxPrice, marker.price)
                        minPrice = min(minPrice, marker.price)
                    }
                }
                val priceRange = (maxPrice - minPrice).coerceAtLeast(0.01)
                val maxVolume = visibleCandles.maxOf { it.volume }.coerceAtLeast(1.0)
                val xStep = size.width / visibleCandles.size.coerceAtLeast(1)

                fun priceY(price: Double): Float {
                    return ((maxPrice - price) / priceRange * priceHeight).toFloat()
                }

                repeat(5) { index ->
                    val y = priceHeight * index / 4f
                    drawLine(
                        color = Color(0xFFECEEF2),
                        start = Offset(0f, y),
                        end = Offset(size.width, y),
                        strokeWidth = 1f,
                    )
                }

                drawLine(
                    color = Color(0xFFECEEF2),
                    start = Offset(0f, volumeTop),
                    end = Offset(size.width, volumeTop),
                    strokeWidth = 1f,
                )

                if (lineMode) {
                    if (fiveDayMode) {
                        drawDaySeparators(
                            candles = visibleCandles,
                            xStep = xStep,
                            chartBottom = volumeTop + volumeHeight,
                        )
                    }
                    drawLineChart(
                        candles = visibleCandles,
                        xStep = xStep,
                        priceY = ::priceY,
                        priceHeight = priceHeight,
                        volumeTop = volumeTop,
                        volumeHeight = volumeHeight,
                        maxVolume = maxVolume,
                    )
                    listOf(
                        visibleCandles.indexOfFirst { it.high == intradayHigh?.high } to
                            StockColors.UpRed,
                        visibleCandles.indexOfFirst { it.low == intradayLow?.low } to
                            StockColors.DownGreen,
                    ).forEach { (index, color) ->
                        if (index >= 0) {
                            val candle = visibleCandles[index]
                            val price = if (color == StockColors.UpRed) candle.high else candle.low
                            drawCircle(
                                color = color,
                                radius = 4.5f,
                                center = Offset(xStep * (index + 0.5f), priceY(price)),
                            )
                        }
                    }
                } else {
                    val pressure = resistance ?: (maxPrice - priceRange * 0.07)
                    val supportLine = support ?: (minPrice + priceRange * 0.48)

                    drawLine(
                        color = StockColors.UpRed.copy(alpha = 0.65f),
                        start = Offset(0f, priceY(pressure)),
                        end = Offset(size.width, priceY(pressure)),
                        strokeWidth = 1.3f,
                        pathEffect = PathEffect.dashPathEffect(floatArrayOf(7f, 5f)),
                    )
                    drawLine(
                        color = StockColors.DownGreen.copy(alpha = 0.65f),
                        start = Offset(0f, priceY(supportLine)),
                        end = Offset(size.width, priceY(supportLine)),
                        strokeWidth = 1.3f,
                        pathEffect = PathEffect.dashPathEffect(floatArrayOf(7f, 5f)),
                    )

                    visibleCandles.forEachIndexed { index, candle ->
                        val x = xStep * (index + 0.5f)
                        val color = when {
                            candle.close > candle.open -> StockColors.UpRed
                            candle.close < candle.open -> StockColors.DownGreen
                            else -> StockColors.Neutral
                        }
                        val bodyWidth = max(2.4f, xStep * 0.58f)

                        drawLine(
                            color = color,
                            start = Offset(x, priceY(candle.high)),
                            end = Offset(x, priceY(candle.low)),
                            strokeWidth = 1.15f,
                        )

                        val openY = priceY(candle.open)
                        val closeY = priceY(candle.close)
                        val top = min(openY, closeY)
                        val bottom = max(openY, closeY)
                        drawRect(
                            color = color,
                            topLeft = Offset(x - bodyWidth / 2f, top),
                            size = Size(bodyWidth, max(1.6f, bottom - top)),
                        )

                        val volumeBarHeight = (candle.volume / maxVolume * volumeHeight).toFloat()
                        drawRect(
                            color = color.copy(alpha = 0.88f),
                            topLeft = Offset(x - bodyWidth / 2f, volumeTop + volumeHeight - volumeBarHeight),
                            size = Size(bodyWidth, volumeBarHeight),
                        )
                    }

                    fun drawSeries(values: List<Double?>, color: Color) {
                        val path = Path()
                        var started = false
                        values.forEachIndexed { index, value ->
                            value ?: return@forEachIndexed
                            val x = xStep * (index + 0.5f)
                            val y = priceY(value)
                            if (!started) {
                                path.moveTo(x, y)
                                started = true
                            } else {
                                path.lineTo(x, y)
                            }
                        }
                        drawPath(path, color = color, style = Stroke(width = 2f))
                    }

                    drawSeries(visibleMa5, Color(0xFFF97316))
                    drawSeries(visibleMa10, Color(0xFF3B82F6))
                    drawSeries(visibleMa20, Color(0xFFA855F7))
                    drawSeries(visibleMa60, Color(0xFF22C55E))

                    if (tradeMarkers.isNotEmpty()) {
                        val byDate = visibleCandles.mapIndexed { index, c ->
                            c.date.take(10) to index
                        }.toMap()
                        tradeMarkers.forEach { marker ->
                            val idx = byDate[marker.date.take(10)] ?: return@forEach
                            val x = xStep * (idx + 0.5f)
                            val y = priceY(marker.price)
                            val buy = marker.side.equals("BUY", ignoreCase = true)
                            val color = if (buy) StockColors.UpRed else StockColors.DownGreen
                            val path = Path().apply {
                                if (buy) {
                                    moveTo(x, y + 7f)
                                    lineTo(x - 5f, y + 16f)
                                    lineTo(x + 5f, y + 16f)
                                } else {
                                    moveTo(x, y - 7f)
                                    lineTo(x - 5f, y - 16f)
                                    lineTo(x + 5f, y - 16f)
                                }
                                close()
                            }
                            drawPath(path, color = color)
                        }
                    }
                }

                visibleRecs.forEach { (marker, absIndex) ->
                    val localIndex = absIndex - visibleStart
                    if (localIndex !in visibleCandles.indices) return@forEach
                    val x = xStep * (localIndex + 0.5f)
                    val y = priceY(marker.price)
                    val active = selectedRecommend?.atMs == marker.atMs &&
                        selectedRecommend?.price == marker.price
                    drawCircle(
                        color = Color.White,
                        radius = if (active) 8.5f else 7f,
                        center = Offset(x, y),
                    )
                    drawCircle(
                        color = Color(0xFFFF8C00),
                        radius = if (active) 6.5f else 5.2f,
                        center = Offset(x, y),
                    )
                }

                selectedIndex
                    ?.minus(visibleStart)
                    ?.takeIf { it in visibleCandles.indices }
                    ?.let { localIndex ->
                        val candle = visibleCandles[localIndex]
                        val x = xStep * (localIndex + 0.5f)
                        val y = priceY(candle.close)
                        drawLine(
                            color = StockColors.TextPrimary.copy(alpha = 0.55f),
                            start = Offset(x, 0f),
                            end = Offset(x, volumeTop + volumeHeight),
                            strokeWidth = 1f,
                            pathEffect = PathEffect.dashPathEffect(floatArrayOf(4f, 4f)),
                        )
                        drawLine(
                            color = StockColors.TextPrimary.copy(alpha = 0.4f),
                            start = Offset(0f, y),
                            end = Offset(size.width, y),
                            strokeWidth = 1f,
                            pathEffect = PathEffect.dashPathEffect(floatArrayOf(4f, 4f)),
                        )
                        drawCircle(
                            color = StockColors.PrimaryBlue,
                            radius = 4f,
                            center = Offset(x, y),
                        )
                    }
                }

            if (showDecorations && !lineMode) {
                ChartBadge(
                    text = "支撑确认",
                    color = StockColors.PrimaryBlue,
                    modifier = Modifier.offset(x = maxWidth * 0.17f, y = maxHeight * 0.42f),
                )
            }
        }

        val dateLabels = remember(visibleCandles, lineMode, fiveDayMode) {
            buildDateLabels(visibleCandles, lineMode, fiveDayMode)
        }

        if (dateLabels.isNotEmpty()) {
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(start = 14.dp, end = 14.dp, top = 2.dp, bottom = 6.dp),
                horizontalArrangement = Arrangement.SpaceBetween,
            ) {
                dateLabels.forEach {
                    Text(
                        it,
                        color = StockColors.Neutral,
                        fontSize = 10.sp,
                        maxLines = 1,
                    )
                }
            }
        }
    }
}

@Composable
private fun IntradayRangeInfo(
    high: Double,
    low: Double,
    referenceClose: Double?,
) {
    fun percent(price: Double): String {
        if (referenceClose == null || referenceClose == 0.0) return "--"
        val value = (price - referenceClose) / referenceClose * 100.0
        return "${if (value > 0.0) "+" else ""}${formatDecimal(value)}%"
    }

    Row(
        modifier = Modifier
            .fillMaxWidth()
            .background(Color(0xFFF8FAFC))
            .padding(horizontal = 14.dp, vertical = 5.dp),
        horizontalArrangement = Arrangement.spacedBy(22.dp),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Text(
            "分时最高  ${formatDecimal(high)}  ${percent(high)}",
            color = StockColors.UpRed,
            fontSize = 12.sp,
            fontWeight = FontWeight.Bold,
        )
        Text(
            "分时最低  ${formatDecimal(low)}  ${percent(low)}",
            color = StockColors.DownGreen,
            fontSize = 12.sp,
            fontWeight = FontWeight.Bold,
        )
        Text(
            "昨收  ${referenceClose?.let { formatDecimal(it) } ?: "--"}",
            color = StockColors.TextSecondary,
            fontSize = 11.sp,
        )
    }
}

@Composable
private fun RecommendPickInfo(
    marker: ChartRecommendMarker,
    currentPrice: Double?,
) {
    val rec = marker.price
    val now = currentPrice?.takeIf { it > 0 }
    val pnl = if (now != null && rec > 0) now - rec else null
    val pnlPct = if (pnl != null && rec > 0) pnl / rec * 100.0 else null
    val pnlColor = when {
        pnl == null || pnl == 0.0 -> StockColors.Neutral
        pnl > 0 -> StockColors.UpRed
        else -> StockColors.DownGreen
    }
    val pnlText = when {
        pnl == null || pnlPct == null -> "盈亏 --"
        pnl > 0 -> "盈利 +${formatDecimal(pnl)}（+${formatDecimal(pnlPct)}%）"
        pnl < 0 -> "亏损 ${formatDecimal(abs(pnl))}（${formatDecimal(pnlPct)}%）"
        else -> "持平 0.00（0.00%）"
    }
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .background(Color(0xFFFFF7ED))
            .padding(horizontal = 14.dp, vertical = 7.dp),
        verticalArrangement = Arrangement.spacedBy(2.dp),
    ) {
        Text(
            "推荐点  ${marker.dateLabel.ifBlank { "--"}}",
            color = Color(0xFFC2410C),
            fontSize = 12.sp,
            fontWeight = FontWeight.SemiBold,
        )
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.spacedBy(16.dp),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Text(
                "推荐价 ${formatDecimal(rec)}",
                color = StockColors.TextPrimary,
                fontSize = 13.sp,
                fontWeight = FontWeight.Bold,
            )
            Text(
                "现价 ${now?.let { formatDecimal(it) } ?: "--"}",
                color = StockColors.TextPrimary,
                fontSize = 13.sp,
                fontWeight = FontWeight.Bold,
            )
            Text(
                pnlText,
                color = pnlColor,
                fontSize = 13.sp,
                fontWeight = FontWeight.Bold,
            )
        }
    }
}

@Composable
private fun ZoomControl(
    text: String,
    enabled: Boolean,
    onClick: () -> Unit,
) {
    Text(
        text = text,
        color = if (enabled) StockColors.PrimaryBlue else StockColors.Border,
        fontSize = 16.sp,
        fontWeight = FontWeight.Bold,
        modifier = Modifier
            .background(Color(0xFFF4F6F8), RoundedCornerShape(4.dp))
            .clickable(enabled = enabled, onClick = onClick)
            .padding(horizontal = 7.dp, vertical = 1.dp),
    )
}

@Composable
private fun SelectedCandleInfo(
    candle: Candle,
    previousClose: Double?,
) {
    val change = previousClose?.let { candle.close - it }
    val changePercent = if (previousClose != null && previousClose != 0.0) {
        change!! / previousClose * 100.0
    } else {
        null
    }
    val trendColor = when {
        change == null -> StockColors.TextSecondary
        change > 0 -> StockColors.UpRed
        change < 0 -> StockColors.DownGreen
        else -> StockColors.Neutral
    }
    val signedChange = change?.let { "${if (it > 0) "+" else ""}${formatDecimal(it)}" } ?: "--"
    val signedPercent = changePercent?.let { "${if (it > 0) "+" else ""}${formatDecimal(it)}%" } ?: "--"

    Row(
        modifier = Modifier
            .fillMaxWidth()
            .background(Color(0xFFF8FAFC))
            .padding(horizontal = 14.dp, vertical = 5.dp),
        horizontalArrangement = Arrangement.spacedBy(10.dp),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Text(candle.date.ifBlank { "--" }, color = StockColors.TextPrimary, fontSize = 11.sp)
        Text("开 ${formatDecimal(candle.open)}", color = StockColors.TextSecondary, fontSize = 11.sp)
        Text("高 ${formatDecimal(candle.high)}", color = StockColors.UpRed, fontSize = 11.sp)
        Text("低 ${formatDecimal(candle.low)}", color = StockColors.DownGreen, fontSize = 11.sp)
        Text("收 ${formatDecimal(candle.close)}", color = StockColors.TextPrimary, fontSize = 11.sp)
        Text("涨跌 $signedChange  $signedPercent", color = trendColor, fontSize = 11.sp, fontWeight = FontWeight.Bold)
        Text("量 ${formatVolume(candle.volume)}", color = StockColors.TextSecondary, fontSize = 11.sp)
    }
}

private fun formatVolume(volume: Double): String = when {
    abs(volume) >= 100_000_000 -> "${formatDecimal(volume / 100_000_000)}亿"
    abs(volume) >= 10_000 -> "${formatDecimal(volume / 10_000)}万"
    else -> formatDecimal(volume, 0)
}

private fun DrawScope.drawDaySeparators(
    candles: List<Candle>,
    xStep: Float,
    chartBottom: Float,
) {
    var prevDay = ""
    candles.forEachIndexed { index, candle ->
        val day = candle.date.take(10)
        if (day.isBlank()) return@forEachIndexed
        if (prevDay.isNotEmpty() && day != prevDay) {
            val x = xStep * index
            drawLine(
                color = Color(0xFFD0D5DD),
                start = Offset(x, 0f),
                end = Offset(x, chartBottom),
                strokeWidth = 1f,
                pathEffect = PathEffect.dashPathEffect(floatArrayOf(4f, 4f)),
            )
        }
        prevDay = day
    }
}

private fun DrawScope.drawLineChart(
    candles: List<Candle>,
    xStep: Float,
    priceY: (Double) -> Float,
    priceHeight: Float,
    volumeTop: Float,
    volumeHeight: Float,
    maxVolume: Double,
) {
    if (candles.isEmpty()) return

    val pricePath = Path()
    val fillPath = Path()
    candles.forEachIndexed { index, candle ->
        val x = xStep * (index + 0.5f)
        val y = priceY(candle.close)
        if (index == 0) {
            pricePath.moveTo(x, y)
            fillPath.moveTo(x, priceHeight)
            fillPath.lineTo(x, y)
        } else {
            pricePath.lineTo(x, y)
            fillPath.lineTo(x, y)
        }
    }
    val lastX = xStep * (candles.lastIndex + 0.5f)
    fillPath.lineTo(lastX, priceHeight)
    fillPath.close()

    drawPath(
        fillPath,
        brush = Brush.verticalGradient(
            colors = listOf(
                StockColors.PrimaryBlue.copy(alpha = 0.22f),
                StockColors.PrimaryBlue.copy(alpha = 0.02f),
            ),
        ),
    )
    drawPath(pricePath, color = StockColors.PrimaryBlue, style = Stroke(width = 2.2f))

    val avgPoints = candles.mapIndexedNotNull { index, candle ->
        val avg = candle.avg ?: return@mapIndexedNotNull null
        index to avg
    }
    if (avgPoints.size >= 2) {
        val avgPath = Path()
        avgPoints.forEachIndexed { i, (index, avg) ->
            val x = xStep * (index + 0.5f)
            val y = priceY(avg)
            if (i == 0) avgPath.moveTo(x, y) else avgPath.lineTo(x, y)
        }
        drawPath(avgPath, color = StockColors.Orange, style = Stroke(width = 1.6f))
    }

    candles.forEachIndexed { index, candle ->
        val prev = candles.getOrNull(index - 1)?.close ?: candle.open
        val color = when {
            candle.close > prev -> StockColors.UpRed
            candle.close < prev -> StockColors.DownGreen
            else -> StockColors.Neutral
        }
        val bodyWidth = max(1.2f, xStep * 0.7f)
        val x = xStep * (index + 0.5f)
        val volumeBarHeight = (candle.volume / maxVolume * volumeHeight).toFloat()
        drawRect(
            color = color.copy(alpha = 0.75f),
            topLeft = Offset(x - bodyWidth / 2f, volumeTop + volumeHeight - volumeBarHeight),
            size = Size(bodyWidth, volumeBarHeight),
        )
    }
}

private fun buildDateLabels(
    candles: List<Candle>,
    lineMode: Boolean,
    fiveDayMode: Boolean,
): List<String> {
    val dated = candles.map { it.date }.filter { it.isNotBlank() }
    if (dated.size < 2) return emptyList()

    if (fiveDayMode) {
        // 每个交易日取一个标签（MM-DD）
        val days = linkedSetOf<String>()
        dated.forEach { raw ->
            val day = raw.take(10)
            if (day.length >= 10) days += day
        }
        if (days.isEmpty()) return emptyList()
        return days.map { it.substring(5) }
    }

    fun labelAt(i: Int): String {
        val raw = dated[i]
        return when {
            // 分时：HH:mm
            lineMode && raw.length >= 16 -> raw.substring(11, 16)
            // 日/周/月：MM-DD
            raw.length >= 10 -> raw.substring(5, 10)
            else -> raw.takeLast(5)
        }
    }

    return listOf(0, dated.size / 4, dated.size / 2, dated.size * 3 / 4, dated.lastIndex)
        .distinct()
        .map(::labelAt)
}

@Composable
private fun Legend(text: String, color: Color) {
    Text(text, color = color, fontSize = 11.sp, fontWeight = FontWeight.Medium)
}

@Composable
private fun ChartBadge(
    text: String,
    color: Color,
    modifier: Modifier = Modifier,
) {
    Box(
        modifier = modifier
            .background(Color.White.copy(alpha = 0.92f), RoundedCornerShape(5.dp))
            .padding(horizontal = 7.dp, vertical = 4.dp),
    ) {
        Text(text, color = color, fontSize = 10.sp, fontWeight = FontWeight.SemiBold)
    }
}

private fun movingAverage(
    candles: List<Candle>,
    period: Int,
): List<Double?> {
    return candles.indices.map { index ->
        if (index + 1 < period) {
            null
        } else {
            val start = index - period + 1
            var sum = 0.0
            for (i in start..index) {
                sum += candles[i].close
            }
            sum / period
        }
    }
}
